import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const checkAuth = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(data.user);
      setRestaurant(data.restaurant);
    } catch {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setRestaurant(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    setRestaurant(data.restaurant);
    return data;
  };

  const register = async (email, password, name, restaurant_name, currency = 'EUR', referral_code = null) => {
    const payload = { email, password, name, restaurant_name, currency };
    if (referral_code) payload.referral_code = referral_code;
    const { data } = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    setRestaurant(data.restaurant);
    return data;
  };

  const googleAuth = async (credential) => {
    const { data } = await axios.post(`${API}/auth/google`, { credential });
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    setRestaurant(data.restaurant);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setRestaurant(null);
    axios.post(`${API}/auth/logout`).catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, restaurant, loading, token, login, register, googleAuth, logout, authHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};
